import { useMemo } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@kollez/design-system';
import { useAuthStore, type Membership } from '@/lib/authStore';
import { useSwitchIfNeeded } from '@/lib/useSwitchIfNeeded';

/**
 * F-Step 104 — the header workspace switcher. Renders as a Radix DropdownMenu triggered by the
 * sb-workspace tile in the sidebar. Sorts verified memberships by relationship priority; Kollez
 * Public is always the last row (non-removable per invariant #2 of the multi-tenant identity
 * build ledger).
 *
 * Single-membership users (staff, unaffiliated students) see the tile as a non-interactive
 * label — no dropdown, no switch mechanic.
 */

const RELATIONSHIP_ORDER: Record<Membership['relationship'], number> = {
  full_time: 1,
  part_time: 2,
  alumni: 3,
  dual_degree: 4,
  intern: 5,
  staff: 6,
  other: 7,
  public: 99,   // Kollez Public always last
};

/** Nice human labels for staff job roles. Falls back to a title-cased slug when unknown. */
const ROLE_LABEL: Record<string, string> = {
  platform_admin: 'Platform admin',
  platform_operator: 'Platform operator',
  platform_viewer: 'Platform viewer',
  group_admin: 'Group admin',
  group_manager: 'Group manager',
  group_accountant: 'Group accountant',
  university_admin: 'University admin',
  university_registrar: 'University registrar',
  university_exam_controller: 'Exam controller',
  principal: 'Principal',
  vice_principal: 'Vice principal',
  tpo: 'TPO',
  assistant_tpo: 'Assistant TPO',
  registrar: 'Registrar',
  accountant: 'Accountant',
  admissions_officer: 'Admissions officer',
  hod: 'HoD',
  placement_coordinator: 'Placement coordinator',
  faculty: 'Faculty',
  mentor_faculty: 'Mentor faculty',
  partner_admin: 'Partner admin',
  recruiter: 'Recruiter',
  hiring_manager: 'Hiring manager',
  partner_accountant: 'Partner accountant',
  technical_panel: 'Technical panel',
};

function formatRoleLabel(role: string): string {
  return ROLE_LABEL[role]
    ?? role.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const RELATIONSHIP_LABEL: Record<Membership['relationship'], string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  alumni: 'Alumni',
  public: 'Public catalog',
  dual_degree: 'Dual degree',
  intern: 'Intern',
  staff: 'Staff',
  other: 'Other',
};

function sortMemberships(list: Membership[]): Membership[] {
  return [...list].sort((a, b) => {
    const ao = RELATIONSHIP_ORDER[a.relationship] ?? 50;
    const bo = RELATIONSHIP_ORDER[b.relationship] ?? 50;
    if (ao !== bo) return ao - bo;
    return a.tenantName.localeCompare(b.tenantName);
  });
}

interface WorkspaceSwitcherProps {
  /** Displayed name + role label when there's only one membership (fallback tile). */
  fallbackLabel: string;
  userName: string;
}

export function WorkspaceSwitcher({ fallbackLabel, userName }: WorkspaceSwitcherProps) {
  const memberships = useAuthStore((s) => s.memberships);
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  const user = useAuthStore((s) => s.user);
  const verified = useMemo(
    () => memberships.filter((m) => m.status === 'verified'),
    [memberships],
  );
  const sorted = useMemo(() => sortMemberships(verified), [verified]);
  const active = sorted.find((m) => m.tenantId === activeTenantId);
  const toast = useToast();
  const switchIfNeeded = useSwitchIfNeeded();

  const displayTitle = active?.tenantName ?? fallbackLabel;
  // For staff members ('staff' relationship) the tenant-relationship label reads flat
  // ("Staff") — the job title (TPO, Principal, HoD…) is far more useful in the tile,
  // and it's already carried on the user record via `role`. Fall through to
  // RELATIONSHIP_LABEL for other kinds (Alumni, Student, etc.), where role wouldn't
  // add anything.
  const roleLabel = user?.role ? formatRoleLabel(user.role) : null;
  const displaySub = active
    ? (active.relationship === 'staff' && roleLabel ? roleLabel : RELATIONSHIP_LABEL[active.relationship])
    : fallbackLabel;

  // Single-membership: static tile, no dropdown.
  if (sorted.length <= 1) {
    return (
      <div className="sb-workspace" aria-label={`Workspace: ${displayTitle}`}>
        <span
          className="logo-tile"
          style={{ width: 30, height: 30, background: 'rgb(var(--bg-inkwell))', color: '#fff', border: 0 }}
        >
          {displayTitle.slice(0, 2).toUpperCase()}
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
            {userName}
          </span>
          <span className="truncate" style={{ display: 'block', fontSize: 12, color: 'rgb(var(--ink-tertiary))' }}>
            {displaySub}
          </span>
        </span>
      </div>
    );
  }

  async function handleSwitch(tenantId: string) {
    try {
      const switched = await switchIfNeeded(tenantId);
      if (switched) {
        const next = sorted.find((m) => m.tenantId === tenantId);
        toast.show({
          title: 'Workspace switched',
          message: next ? `You're now in ${next.tenantName}.` : undefined,
        });
      }
    } catch (err) {
      toast.show({
        title: "Couldn't switch workspace",
        message: err instanceof Error ? err.message : 'Please try again.',
        tone: 'danger',
      });
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="sb-workspace"
          aria-label={`Switch workspace. Current: ${displayTitle}`}
        >
          <span
            className="logo-tile"
            style={{ width: 30, height: 30, background: 'rgb(var(--bg-inkwell))', color: '#fff', border: 0 }}
          >
            {displayTitle.slice(0, 2).toUpperCase()}
          </span>
          <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
            <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
              {displayTitle}
            </span>
            <span className="truncate" style={{ display: 'block', fontSize: 12, color: 'rgb(var(--ink-tertiary))' }}>
              {displaySub}
            </span>
          </span>
          <ChevronsUpDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        style={{ minWidth: 260, maxHeight: 360, overflowY: 'auto' }}
      >
        <DropdownMenuLabel>Your workspaces</DropdownMenuLabel>
        {sorted.map((m) => {
          const isActive = m.tenantId === activeTenantId;
          return (
            <DropdownMenuItem
              key={m.tenantId}
              onSelect={(e) => {
                e.preventDefault();
                void handleSwitch(m.tenantId);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span
                aria-hidden="true"
                style={{ width: 16, display: 'inline-flex', justifyContent: 'center', color: 'rgb(var(--ink-primary))' }}
              >
                {isActive ? <Check size={14} /> : null}
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  className="truncate"
                  style={{ display: 'block', fontSize: 13, fontWeight: isActive ? 600 : 500 }}
                >
                  {m.tenantName}
                </span>
                <span
                  className="truncate"
                  style={{ display: 'block', fontSize: 11, color: 'rgb(var(--ink-tertiary))' }}
                >
                  {RELATIONSHIP_LABEL[m.relationship]}
                  {m.primary ? ' · Primary' : ''}
                </span>
              </span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <NavLink to="/profile" style={{ display: 'block', fontSize: 12 }}>
            Manage memberships
          </NavLink>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
